import { combineReducers, createReducer, on } from '@ngrx/store';
import { addFinancialMetrics, addModelPortfolioId, addUserSpecificPortfolioId, clearRiskLevelRange, removeFinancialMetric, removeModelPortfolioId, removeUserSpecificPortfolioId, setFinancialMetrics, setModelPortfolioIds, setRiskProfile, setRiskProfileActiveBenchmark, setRiskProfileActiveDescription, setRiskProfileActiveDisplayName, setRiskProfileActiveName, setRiskProfileBroadAssetClassActive, setRiskProfileError, setRiskProfileRiskLevelRangeMax, setRiskProfileRiskLevelRangeMin, setRiskProfileRiskLevelValue, setRiskProfileRiskProfileId, setUserSpecificPortfolioIds, updateFinancialMetric, } from './risk-profile.actions';
const riskProfilesInitialState = {
    description: '',
    name: '',
    display_name: '',
    benchmark_symbol: '',
    model_portfolio_ids: [],
    user_specific_portfolio_ids: [],
    risk_level_value: '',
    risk_level_range_min: '',
    risk_level_range_max: '',
    broad_asset_class_targets: [
        { name: 'Stock', target: 0, low: null, high: null },
        { name: 'Bond', target: 0, low: null, high: null },
        { name: 'Cash', target: 0, low: null, high: null },
        { name: 'Preferred', target: 0, low: null, high: null },
        { name: 'Convertible', target: 0, low: null, high: null },
        { name: 'Other', target: 0, low: null, high: null },
    ],
    financial_metrics: [],
    sharing: {},
};
const riskProfileActive = createReducer({ ...riskProfilesInitialState }, on(setRiskProfile, (state, { riskProfile }) => ({
    ...state,
    ...riskProfile,
})), on(setRiskProfileActiveBenchmark, (state, { benchmark }) => ({
    ...state,
    benchmark_symbol: benchmark,
})), on(setRiskProfileActiveDescription, (state, { description }) => ({
    ...state,
    description,
})), on(setRiskProfileActiveName, (state, { name }) => ({
    ...state,
    name,
})), on(setRiskProfileActiveDisplayName, (state, { displayName }) => ({
    ...state,
    display_name: displayName,
})), on(setModelPortfolioIds, (state, action) => ({
    ...state,
    model_portfolio_ids: action.modelPortfolioIds,
})), on(addModelPortfolioId, (state, action) => {
    return {
        ...state,
        model_portfolio_ids: [...state.model_portfolio_ids, action.modelPortfolioId],
    };
}), on(removeModelPortfolioId, (state, action) => {
    return {
        ...state,
        model_portfolio_ids: [
            ...state.model_portfolio_ids.slice(0, action.index),
            ...state.model_portfolio_ids.slice(action.index + 1),
        ],
    };
}), on(setUserSpecificPortfolioIds, (state, action) => ({
    ...state,
    user_specific_portfolio_ids: action.userSpecificPortfolioIds,
})), on(addUserSpecificPortfolioId, (state, action) => {
    return {
        ...state,
        user_specific_portfolio_ids: [...state.user_specific_portfolio_ids, action.modelPortfolioId],
    };
}), on(removeUserSpecificPortfolioId, (state, action) => {
    return {
        ...state,
        user_specific_portfolio_ids: [
            ...state.user_specific_portfolio_ids.slice(0, action.index),
            ...state.user_specific_portfolio_ids.slice(action.index + 1),
        ],
    };
}), on(setRiskProfileRiskLevelValue, (state, { risk_level_value }) => ({
    ...state,
    risk_level_value,
})), on(setRiskProfileRiskLevelRangeMin, (state, { risk_level_range_min }) => ({
    ...state,
    risk_level_range_min,
})), on(setRiskProfileRiskLevelRangeMax, (state, { risk_level_range_max }) => ({
    ...state,
    risk_level_range_max,
})), on(clearRiskLevelRange, (state) => ({
    ...state,
    risk_level_range_min: '',
    risk_level_range_max: '',
})), on(setRiskProfileBroadAssetClassActive, (state, { name, field, value }) => {
    const broadAssetClassTarget = state.broad_asset_class_targets.find((target) => target.name === name);
    if (broadAssetClassTarget) {
        const newTarget = { ...broadAssetClassTarget };
        if (field === 'target') {
            newTarget[field] = value && !Number.isNaN(value) ? value : 0;
        }
        else if (field === 'low' || field === 'high') {
            newTarget[field] = value;
        }
        const updatedBroadAssetClassTargets = state.broad_asset_class_targets.map((target) => target.name === name ? newTarget : target);
        return {
            ...state,
            broad_asset_class_targets: updatedBroadAssetClassTargets,
        };
    }
    return state;
}), on(addFinancialMetrics, (state, { metrics }) => {
    const existingMetricNames = state.financial_metrics.map((metric) => metric.name);
    const newMetrics = metrics.filter((metric) => !existingMetricNames.includes(metric.name));
    return {
        ...state,
        financial_metrics: [...state.financial_metrics, ...newMetrics],
    };
}), on(setFinancialMetrics, (state, { metrics }) => {
    return {
        ...state,
        financial_metrics: metrics,
    };
}), on(updateFinancialMetric, (state, { name, field, value }) => {
    const updatedMetrics = state.financial_metrics.map((metric) => metric.name === name ? { ...metric, [field]: value } : metric);
    return {
        ...state,
        financial_metrics: updatedMetrics,
    };
}), on(removeFinancialMetric, (state, { name }) => ({
    ...state,
    financial_metrics: state.financial_metrics.filter((metric) => metric.name !== name),
})));
const riskProfileSaved = createReducer({ ...riskProfilesInitialState }, on(setRiskProfile, (state, { riskProfile }) => ({
    ...state,
    ...riskProfile,
})));
export const riskProfileId = createReducer(null, on(setRiskProfileRiskProfileId, (state, { id }) => id));
// This function returns the error message corresponding to the status code
// and error name of an HttpErrorResponse
const _getErrorMessage = (errorResponse) => {
    if (errorResponse.status === 400 && errorResponse.error) {
        const errorMessages = [];
        Object.entries(errorResponse.error).forEach(([_, messages]) => {
            if (Array.isArray(messages)) {
                errorMessages.push(`${messages.join(' ')}`);
            }
        });
        return errorMessages.join(' ');
    }
    if (errorResponse.status === 403 && errorResponse.error?.error_code === 'owner_no_access') {
        return errorResponse.error?.error_message;
    }
    return 'Something went wrong when trying to save the risk profile.';
};
export const riskProfileError = createReducer({ status: null, message: null }, on(setRiskProfileError, (state, { errorResponse }) => ({
    status: errorResponse.status,
    message: _getErrorMessage(errorResponse),
})));
export const riskProfile = combineReducers({
    riskProfileActive,
    riskProfileSaved,
});
